import { React, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Suspense } from "react";

import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";

import { getIndividualStudentResults } from "../services/APIClient";
import { BigSpinner } from "../services/SpinnerComponent";

import "./IndividualStudentResultsOverview.css";
import "./ClassOverview.css";
import "./PrintStudentResults.css";

import ReturnToResults from "./ReturnToResults";
import ReturnToClassResults from "./RetrunToClassResults";
import SENWatchlistButton from "./SENWatchlistButton";
import ResultsHistory from "./ResultsHistory";
import RecommendationsButton from "./RecommendationsButton";

import ScoreInterpretationTable from "./ScoreInterpretationTable";

import PrintActionIndividual from "./PrintActionIndividual";
import { preprocessStudentResults } from "../services/GroupAndAverageTasks";

function IndividualStudentResultsOverview(props) {
  const {
    classDetails,
    classAssessments,
    gradeLevel,
    teachers,
    assessment,
    student,
    assessmentResultsByStudent,
    schoolid,
    teacherid,
    fromSencoResults,
    readingScores,
  } = props;

  console.log(assessment, "ASSESSMENT PROPS");

  // All hooks must be called before any early return
  const [individualStudentResult, setIndividualStudentResult] = useState([]);
  const [toggleScoreType, setToggleScoreType] = useState("");
  const [grade1Results, setGrade1Results] = useState([]);
  const [grade2Results, setGrade2Results] = useState([]);
  const [allResults, setAllResults] = useState([]); // Added for all results
  const [grade1Prefix, setGrade1Prefix] = useState("");
  const [grade2Prefix, setGrade2Prefix] = useState("");
  const [showCurrentOnly, setShowCurrentOnly] = useState(true);
  const [loading, setLoading] = useState(true); // Added loading state
  const [activeTab, setActiveTab] = useState(() => {
    if (assessment.grade_level1) return "grade1";
    if (assessment.grade_level2) return "grade2";
    return "all"; // Default to "all" if neither grade level matches
  });
  const [toggleChanged, setToggleChanged] = useState(false); // Track if toggle has been changed due to tab switch
  const [showPrintCard, setShowPrintCard] = useState(false);
  const [printCardPosition, setPrintCardPosition] = useState({
    top: 0,
    left: 0,
  });

    useEffect(() => {
  console.log("Applying sorting...");
  // Force a re-render after a short delay to ensure sorting is applied
  if (allResults.length > 0) {
    const timer = setTimeout(() => {
      console.log("Forcing UI update for sorted results");
      setActiveTab(prev => prev); // This forces a re-render
    }, 100);
    return () => clearTimeout(timer);
  }
}, [allResults]);

  useEffect(() => {
    if (!student || !student.id) return; // Don't fetch if no student
    setLoading(true);
    getIndividualStudentResults(student.id)
      .then((response) => {
        setIndividualStudentResult(response);

        const processedResults = preprocessStudentResults(response);
        setAllResults(processedResults); // Store all processed results

        const grade1 = processedResults.filter(
          (result) => result.grade_level1
        );
        const grade2 = processedResults.filter(
          (result) => result.grade_level2
        );
        setGrade1Results(grade1);
        setGrade2Results(grade2);

        if (grade1.length > 0) {
          setGrade1Prefix(grade1[0].name.split(" ")[0]);
        }
        if (grade2.length > 0) {
          setGrade2Prefix(grade2[0].name.split(" ")[0]);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [student?.id]); // Only run if student.id changes

  // Function to sort assessment types (baseline, midpoint, etc.)
  const sortByAssessmentType = (results) => {
    return [...results].sort((a, b) => {
      const getSortKey = (name) => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4; // Default for other assessments
      };
      
      return getSortKey(a.name) - getSortKey(b.name);
    });
  };

  // Guard: Ensure student is defined and has id (after hooks)
  if (!student || !student.id) {
    return (
      <div>
        <h3>No student selected or student data missing.</h3>
      </div>
    );
  }

  const { id: studentid, name: studentName } = student;
  const {
    grade_level1: gradeLevel1,
    grade_level2: gradeLevel2,
    trial_class: trialClass,
  } = classDetails;

  const filteredGrade1Results = showCurrentOnly
    ? grade1Results.filter((result) => result.name === assessment.name)
    : grade1Results;

  const filteredGrade2Results = showCurrentOnly
    ? grade2Results.filter((result) => result.name === assessment.name)
    : grade2Results;

  const filteredAllResults = showCurrentOnly
    ? allResults.filter((result) => result.name === assessment.name)
    : allResults;

  // More explicit approach for sorting "All Results"
  const sortedAllResults = [
    ...filteredAllResults.filter(result => result.grade_level1).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    }),
    ...filteredAllResults.filter(result => !result.grade_level1 && result.grade_level2).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    })
  ];

  // Sort grade1 and grade2 results separately
  const sortedGrade1Results = sortByAssessmentType(filteredGrade1Results);
  const sortedGrade2Results = sortByAssessmentType(filteredGrade2Results);

  const filteredIndividualResults = showCurrentOnly
    ? preprocessStudentResults(individualStudentResult).filter(
      (result) => result.name === assessment.name
    )
    : preprocessStudentResults(individualStudentResult);

  // Sort the individual results with the same explicit approach
  const sortedIndividualResults = [
    ...filteredIndividualResults.filter(result => result.grade_level1).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    }),
    ...filteredIndividualResults.filter(result => !result.grade_level1 && result.grade_level2).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    })
  ];

  // Toggle function
  const toggleView = () => {
    setShowCurrentOnly(!showCurrentOnly);
  };

  const handleTabSelect = (key) => {
    setActiveTab(key);
    if (!toggleChanged) {
      setShowCurrentOnly(!showCurrentOnly); // Change the toggle state
      setToggleChanged(true); // Ensure this only happens once
    }
  };

  console.log(allResults, "ALL RESULTS");
  console.log(sortedAllResults, "SORTED ALL RESULTS");
  console.log(grade1Results, "GRADE 1 RESULTS");  
  console.log(sortedGrade1Results, "SORTED GRADE 1 RESULTS");
  console.log(grade2Results, "GRADE 2 RESULTS");
  console.log(sortedGrade2Results, "SORTED GRADE 2 RESULTS");
  console.log(individualStudentResult, "INDIVIDUAL STUDENT RESULT");
  console.log(sortedIndividualResults, "SORTED INDIVIDUAL STUDENT RESULT");

  if (loading) {
    return <BigSpinner />;
  }


  return (
    <div>
      <style>
        {`
          @media print {
            .alpaca-print-footer {
              display: block !important;
            }
          }
        `}
      </style>
      <Row className="inline-block align-items-center push-down">
        <Col className="col-auto">
          <Suspense fallback={<div>Loading Navigation Button...</div>}>
            <ReturnToClassResults
              classDetails={classDetails}
              teachers={teachers}
              classAssessments={classAssessments}
              assessment={assessment}
              schoolid={schoolid}
              teacherid={teacherid}
              fromSencoResults={fromSencoResults}
            />
          </Suspense>
        </Col>
        <Col className="flex-grow-1 text-center" style={{ margin: "10px" }}>
          <h2 className="page-description-header-results">Results Overview</h2>
        </Col>
        <Col className="col-auto d-flex justify-content-end">
          {fromSencoResults && (
            <SENWatchlistButton
              schoolid={schoolid}
              teachers={teachers}
              teacherid={teacherid}
              assessment={assessment}
              readingScores={readingScores}
            />
          )}
          <button
            className="print-button"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPrintCardPosition({
                top: rect.top + window.scrollY,
                left: rect.left - 250,
              });
              setShowPrintCard(!showPrintCard);
            }}
          >
            Print
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>
          <button className="print-button">
            Share
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>
        </Col>
      </Row>
      <div className="class-overview-container">
        <Container className="container-shape">
          <Row>
            <Col className="col-3 pr-2">
              {" "}
              <h3 className="report-card-header">
                Report Card: {student.name}
              </h3>
            </Col>
          </Row>
          <Row className="page-description-bar align-items-center">
            <Col className="col-auto">
              <Suspense
                fallback={<div>Loading Assessment Dropdown...</div>}
              ></Suspense>
            </Col>
            <Col className="d-flex justify-content-end align-items-center">
              <Suspense fallback={<div>Loading Recommendations...</div>}>
                <RecommendationsButton onResultsPage={true} />
              </Suspense>
              <Suspense fallback={<div>Loading score table...</div>}>
                <ScoreInterpretationTable />
              </Suspense>
            </Col>
          </Row>

          <Row>
            <div className="toggle-container">
              <h5 className="page-description-header">
                Toggle between All Assessments and Current{" "}
              </h5>
              <label className="switch">
                <input
                  type="checkbox"
                  onChange={toggleView}
                  checked={showCurrentOnly}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </Row>
          <Row className="button-container">
            <Col className="col-2"></Col>
          </Row>

          <Row className="button-container">
            <Col className="col-2">
              {/* <GradeCard gradeLevel={gradeLevel} /> */}
            </Col>
            <Col className="col-2">
              {/* <ScoreToggleButton toggleScoreType={toggleScoreType} /> */}
            </Col>
          </Row>

          <br></br>
          {allResults.length > 0 ? (
            <Tabs
              defaultActiveKey={activeTab}
              id="grade-tabs"
              className="custom-tabs"
              activeKey={activeTab}
              onSelect={handleTabSelect}
              style={{ marginBottom: "16px" }}
            >
              {/* All tab */}
              <Tab eventKey="all" title="All Results">
                <Row>
                  <Col className="col-3"></Col>
                  <Col className="col-8"></Col>
                </Row>
                {allResults.length > 0 ? (
                  <ResultsHistory
                    individualStudentResult={sortedAllResults} // Use sorted version here
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                  />
                ) : (
                  <Row>
                    <Col>
                      <p>No answers registered for this student.</p>
                    </Col>
                  </Row>
                )}
              </Tab>

              {/* Only show Grade 1 tab if there are Grade 1 results */}
              {grade1Results.length > 0 && (
                <Tab eventKey="grade1" title={`${grade1Prefix} Level Results`}>
                  <Row>
                    <Col className="col-3"></Col>
                    <Col className="col-3"></Col>
                    <Col className="col-8"></Col>
                  </Row>
                  <ResultsHistory
                    individualStudentResult={sortedGrade1Results} // Use sorted version
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                  />
                </Tab>
              )}

              {/* Only show Grade 2 tab if there are Grade 2 results */}
              {grade2Results.length > 0 && (
                <Tab eventKey="grade2" title={`${grade2Prefix} Level Results`}>
                  <Row>
                    <Col className="col-3"></Col>
                    <Col className="col-8"></Col>
                  </Row>
                  <ResultsHistory
                    individualStudentResult={sortedGrade2Results} // Use sorted version
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                  />
                </Tab>
              )}
            </Tabs>
          ) : (
            <Row>
              <Col>
                <p>
                  No answers registered for this student. Please rescan the
                  student to start the screener.
                </p>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      {showPrintCard && (
        <PrintActionIndividual
          cardPosition={printCardPosition}
          handleCloseCard={() => setShowPrintCard(false)}
          individualStudentResult={
            activeTab === "all" 
              ? sortedAllResults        // Use sorted version for All tab
              : activeTab === "grade1" 
                ? sortedGrade1Results   // Use sorted version for Grade 1
                : sortedGrade2Results   // Use sorted version for Grade 2
          }
          toggleScoreType={toggleScoreType}
          assessmentResultsByStudent={assessmentResultsByStudent}
          student={student}
          classDetails={classDetails}
        />
      )}
    </div>
  );
}

export default IndividualStudentResultsOverview;